import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { APILABOURMGMT } from 'apihandler/labourmgmt';
import { APIMASTER } from 'apihandler/master';

import {
  getSubContractorBilling,
  setSites,
  setSubContractorBilling,
  setSupplierCategories,
  setSuppliers,
  setWorkOrders,
  setUsers,
} from 'component/cellRenderers/renderers';


async function fetchFoodAdvancesInternal(d1, d2) {
  await APIMASTER.SiteOptions().then(r => r.json()).then(r => { if (r.statusCode === 200) setSites(r.response); });
  await APIMASTER.SupplierCategories().then(r => r.json()).then(r => { if (r.statusCode === 200) setSupplierCategories(r.response); });
  await APIMASTER.SupplierOptions().then(r => r.json()).then(r => { if (r.statusCode === 200) setSuppliers(r.response); });
  await APILABOURMGMT.WorkOrderOptions().then(r => r.json()).then(r => { if (r.statusCode === 200) setWorkOrders(r.response); });

  const scb = await APILABOURMGMT.SubcontractorBillingOptions()
    .then(r => r.json())
    .then(r => {
      if (r.statusCode === 200) { setSubContractorBilling(r.response); return r.response; }
      return [];
    });

  await APIMASTER.UserOptions().then(r => r.json()).then(r => { if (r.statusCode === 200) setUsers(r.response); });

  // main data
  const dataResp = await APILABOURMGMT.FoodAdvances(
    d1.format('YYYY-MM-DD'),
    d2.format('YYYY-MM-DD')
  ).then(r => r.json());

  if (dataResp.statusCode !== 200) {
    throw new Error(dataResp.message || 'Failed loading food advances');
  }

  const data = (dataResp.response || []).sort((a, b) => b.id - a.id);

  // enrichment (same logic you had)
  const subContractorBilling = getSubContractorBilling() || scb || [];
  data.forEach(x => {
    const reqd = subContractorBilling.filter(y => y.value === x.contractId);
    if (reqd.length > 0) {
      x.contractorId = reqd[0].dependentvalue;
      x.locationId   = reqd[0].otherdependentvalue;
    }
  });

  return data;
}

// ===== thunk =====
export const loadFoodAdvances = createAsyncThunk(
  'foodAdvance/load',
  async ({ from, to }, { rejectWithValue }) => {
    try {
      const rows = await fetchFoodAdvancesInternal(dayjs(from), dayjs(to));
      return rows;
    } catch (e) {
      return rejectWithValue(e.message || 'Load failed');
    }
  }
);

// ===== slice =====
const initialState = {
  dateValue: dayjs().startOf('month').format('YYYY-MM-DD'),
  toDateValue: dayjs().endOf('month').format('YYYY-MM-DD'),
  rows: [],
  grid: {
    filterModel: null,
    sortModel: [],
    columnState: [],
    quickFilterText: '',
    paginationPage: 0,
  },
  selectedRowIds: [],
  hasInitializedOnce: false,
  loading: false,
  error: undefined,
};

const foodAdvanceSlice = createSlice({
  name: 'foodAdvance',
  initialState,
  reducers: {
    setDates(state, action) {
      state.dateValue = action.payload.from;
      state.toDateValue = action.payload.to;
    },
    setGridFilterModel(state, action) {
      state.grid.filterModel = action.payload;
    },
    setGridSortModel(state, action) {
      state.grid.sortModel = action.payload || [];
    },
    setGridColumnState(state, action) {
      state.grid.columnState = action.payload || [];
    },
    setGridQuickFilter(state, action) {
      state.grid.quickFilterText = action.payload || '';
    },
    setGridPaginationPage(state, action) {
      state.grid.paginationPage = action.payload || 0;
    },
    setSelectedRowIds(state, action) {
      state.selectedRowIds = action.payload || [];
    },
    setHasInitializedOnce(state, action) {
      state.hasInitializedOnce = !!action.payload;
    },
    removeRowsByIds(state, action) {
      const ids = new Set(action.payload || []);
      state.rows = state.rows.filter(r => !ids.has(r.id));
      state.selectedRowIds = state.selectedRowIds.filter(id => !ids.has(id));
    },
    upsertRow(state, action) {
      const row = action.payload;
      const i = state.rows.findIndex(r => r.id === row.id);
      if (i >= 0) state.rows[i] = row;
      else state.rows.unshift(row);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadFoodAdvances.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(loadFoodAdvances.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload || [];
      })
      .addCase(loadFoodAdvances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error';
      });
  }
});

export const {
  setDates,
  setGridFilterModel,
  setGridSortModel,
  setGridColumnState,
  setGridQuickFilter,
  setGridPaginationPage,
  setSelectedRowIds,
  setHasInitializedOnce,
  removeRowsByIds,
  upsertRow,
} = foodAdvanceSlice.actions;

export default foodAdvanceSlice.reducer;
